import React, { useEffect, useState, Suspense, lazy } from "react";
import { brand } from "../brand";
import { isNativeApp, isLowPowerClient } from "../lib/platform";
const WordParticles = lazy(() => import("./three/WordParticles"));

// The signature "weave": the WYVN name assembles from rising particles. It's a
// lightweight Canvas-2D animation (no WebGL), so we now show it on phones too —
// with fewer particles on low-power devices (LITE). Only users who ask for reduced
// motion get a static wordmark instead.
const REDUCED = (() => {
  try {
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  } catch { return false; }
})();
const LITE_PARTICLES = (() => {
  try { return isLowPowerClient(); } catch { return false; }
})();

export default function SplashScreen({ onDone }) {
  const [stage, setStage] = useState(0); // 0 form, 1 acronym, 2 tagline, 3 iris-out

  useEffect(() => {
    // Reduced-motion users get a quick static splash; everyone else sees the full weave.
    const t = REDUCED ? [300, 550, 750, 950] : [900, 1500, 2000, 2400];
    const timers = [
      setTimeout(() => setStage(1), t[0]),
      setTimeout(() => setStage(2), t[1]),
      setTimeout(() => setStage(3), t[2]),
      setTimeout(() => onDone && onDone(), t[3]),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  return (
    <div
      data-testid="splash-screen"
      onClick={() => onDone && onDone()}
      className="fixed inset-0 z-[100] flex cursor-pointer items-center justify-center overflow-hidden bg-ink"
      style={stage === 3 ? { animation: "aeva-iris-out 0.8s cubic-bezier(0.7,0,0.84,0) forwards" } : undefined}
    >
      {/* subtle ambient */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand/16 blur-[150px]" style={{ animation: "aeva-aurora 7s ease-in-out infinite" }} />

      <div className="relative flex flex-col items-center">
        {/* plain logo */}
        <img
          src="/brand/wyvn-symbol.png"
          alt=""
          aria-hidden="true"
          width={210}
          height={210}
          style={{
            objectFit: "contain",
            animation: "phoenix-rise 1.6s cubic-bezier(0.22,1,0.36,1) 0.5s both",
          }}
        />

        {/* the name is formed by rising particles (the signature "weave") — or a crisp
            wordmark for reduced-motion users */}
        <div className="mt-4">
          {REDUCED ? (
            <div style={{ width: 460, height: 150, maxWidth: "80vw" }} className="flex items-center justify-center font-display text-5xl font-bold tracking-wide text-mist">{brand.wordmark}</div>
          ) : (
            <Suspense fallback={<div style={{ width: 460, height: 150, maxWidth: "80vw" }} className="flex items-center justify-center font-display text-5xl font-bold tracking-wide text-mist">{brand.wordmark}</div>}>
              <WordParticles text={brand.wordmark} width={460} height={150} lite={LITE_PARTICLES} />
            </Suspense>
          )}
        </div>

        {/* acronym */}
        <div
          className="-mt-3 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-soft/90 sm:text-xs"
          style={{ opacity: stage >= 1 ? 1 : 0, animation: stage >= 1 ? "wyvn-thread-in 0.7s cubic-bezier(0.22,1,0.36,1) both" : "none" }}
        >
          {brand.acronym.map((w, i) => (
            <React.Fragment key={w}>
              {i > 0 && <span className="text-brand/40">·</span>}
              <span>{w}</span>
            </React.Fragment>
          ))}
        </div>

        {/* tagline */}
        <p
          className="mt-4 text-sm font-medium text-mist-muted"
          style={{ opacity: stage >= 2 ? 1 : 0, animation: stage >= 2 ? "aeva-rise 0.7s cubic-bezier(0.22,1,0.36,1) both" : "none" }}
        >
          {brand.tagline}
        </p>
      </div>
    </div>
  );
}
